import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LysaraLogo } from "@/components/ui/lysara-logo";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import { 
  ArrowRight, 
  CheckCircle, 
  Truck, 
  Stethoscope, 
  ShoppingBag, 
  Wrench,
  Users,
  Globe,
  Shield,
  Zap,
  BarChart3,
  Smartphone
} from "lucide-react";

const Landing = () => {
  const navigate = useNavigate();

  // Handle "Get Started" clicks - use device detection flow
  const handleGetStarted = () => {
    // Navigate to root first to trigger device detection
    // DeviceDetectionGuard will handle the proper routing
    navigate('/');
    // Small delay to allow device detection to run, then navigate to auth
    setTimeout(() => {
      navigate('/auth');
    }, 100);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <LysaraLogo imgClassName="h-8 w-auto" />
            
            <div className="flex items-center gap-4">
              <nav className="hidden md:flex items-center gap-6">
                <a href="#features" className="text-muted-foreground hover:text-primary transition-colors">
                  Features
                </a>
                <a href="#industries" className="text-muted-foreground hover:text-primary transition-colors">
                  Industries
                </a>
                <a href="#pricing" className="text-muted-foreground hover:text-primary transition-colors">
                  Pricing
                </a>
              </nav>
              <Button onClick={handleGetStarted}>Get Started</Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-hero text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge className="bg-white/20 text-white border-white/30">
              Industry-Agnostic Platform
            </Badge>
            
            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              Streamline Your 
              <span className="block bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                Operations
              </span>
              with Lysara
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              The flexible platform that adapts to any industry. From retail delivery to field service, 
              healthcare to logistics — one solution for all your operational needs.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90" onClick={handleGetStarted}>
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-green-600 bg-green-600 text-white hover:border-green-700 hover:bg-green-700 hover:text-white">
                Schedule Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold">Why Choose Lysara?</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Built for flexibility, designed for growth. Our platform adapts to your business, not the other way around.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="hover:shadow-brand transition-all duration-300">
              <CardHeader>
                <Globe className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Industry Agnostic</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Works seamlessly across retail, healthcare, field service, logistics, and any custom workflow you need.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-brand transition-all duration-300">
              <CardHeader>
                <Zap className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Dynamic Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Custom fields, workflows, and forms that adapt to your business processes without code changes.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-brand transition-all duration-300">
              <CardHeader>
                <Smartphone className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Mobile-First Design</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Native mobile experience with offline capabilities for field teams and on-the-go operations.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-brand transition-all duration-300">
              <CardHeader>
                <BarChart3 className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Real-Time Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Live tracking, performance metrics, and insights to optimize your operations in real-time.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-brand transition-all duration-300">
              <CardHeader>
                <Shield className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Enterprise Security</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Multi-tenant architecture with role-based access control and enterprise-grade security.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-brand transition-all duration-300">
              <CardHeader>
                <Users className="h-12 w-12 text-primary mb-4" />
                <CardTitle>Team Collaboration</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Seamless coordination between dispatch, field teams, and management with live updates.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold">Built for Every Industry</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              One platform, endless possibilities. See how Lysara adapts to your specific industry needs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="text-center hover:shadow-brand transition-all duration-300 group">
              <CardHeader>
                <Truck className="h-16 w-16 text-primary mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <CardTitle>Logistics & Delivery</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Route optimization, package tracking, driver management, and proof of delivery.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-brand transition-all duration-300 group">
              <CardHeader>
                <ShoppingBag className="h-16 w-16 text-primary mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <CardTitle>Retail Operations</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Inventory management, order fulfillment, customer communications, and store operations.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-brand transition-all duration-300 group">
              <CardHeader>
                <Stethoscope className="h-16 w-16 text-primary mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <CardTitle>Healthcare Services</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Home visits, patient scheduling, care coordination, and compliance tracking.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-brand transition-all duration-300 group">
              <CardHeader>
                <Wrench className="h-16 w-16 text-primary mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <CardTitle>Field Service</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Work orders, technician dispatch, equipment tracking, and maintenance scheduling.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-12">Trusted by Teams Worldwide</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-muted/30">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <CheckCircle key={i} className="h-5 w-5 text-primary" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "Lysara transformed our delivery operations. The flexibility to customize workflows for different product types has been game-changing."
                </p>
                <div>
                  <p className="font-semibold">Sarah Johnson</p>
                  <p className="text-sm text-muted-foreground">Operations Manager, RetailCorp</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-muted/30">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <CheckCircle key={i} className="h-5 w-5 text-primary" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "The multi-tenant architecture was perfect for managing multiple service locations. Implementation was seamless."
                </p>
                <div>
                  <p className="font-semibold">Michael Chen</p>
                  <p className="text-sm text-muted-foreground">CTO, ServiceFirst</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-muted/30">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <CheckCircle key={i} className="h-5 w-5 text-primary" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "Real-time tracking and mobile apps have improved our field team efficiency by 40%. Highly recommended!"
                </p>
                <div>
                  <p className="font-semibold">Emma Rodriguez</p>
                  <p className="text-sm text-muted-foreground">Field Operations Director, HealthCare+</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-primary text-white">
        <div className="container mx-auto text-center space-y-8">
          <h2 className="text-4xl font-bold">Ready to Transform Your Operations?</h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Join thousands of businesses that have streamlined their operations with Lysara.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button size="lg" className="bg-white text-primary hover:bg-white/90" onClick={handleGetStarted}>
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
              Contact Sales
            </Button>
          </div>
          
          <p className="text-blue-100 text-sm">
            No credit card required • 14-day free trial • Setup in minutes
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t bg-card">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <LysaraLogo className="mb-4" imgClassName="h-8 w-auto" />
              <p className="text-muted-foreground text-sm">
                The industry-agnostic platform that adapts to your business needs.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-3">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-primary transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Industries</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Integrations</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">API Docs</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-3">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-primary transition-colors">About</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-3">Support</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-primary transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Status</a></li>
                <li><Link to="/auth" className="hover:text-primary transition-colors">Login</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>&copy; 2024 Lysara. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
